import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
	Autocomplete,
	Box,
	Button,
	Grid2,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { AREA_COLOR_MAP } from "@/shared/colors";
import { useDirtyValues } from "@/shared/hooks/useDirtyValues";
import {
	ALL_PRODUCT_AREAS,
	PRODUCT_AREA_ASSET_ID,
	PRODUCT_AREA_LABEL,
	PRODUCT_INITIAL_VALUES,
	Product,
	ProductArea,
	VALID_PARENT_AREAS,
	canHaveParent,
	getProductArea,
} from "@/shared/models/Product";
import { getProducts } from "@/shared/repositories/ProductRepository";
import { CatalogSectionSelector } from "@/shared/selectors/CatalogSectionSelector";
import { CustomerSelector } from "@/shared/selectors/CustomerSelector";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { useProductMutations } from "../hooks/useProductMutations";
import { ProductPickerModal } from "./ProductPickerModal";

type Props = {
	product: Product | null;
	modal: "create" | "delete" | "";
	onHide: (shouldUpdate?: boolean) => void;
	onNavigate?: (productId: number) => void;
};

const AREA_OPTIONS: { value: Exclude<ProductArea, null>; label: string }[] =
	ALL_PRODUCT_AREAS.map((value) => ({
		value,
		label: PRODUCT_AREA_LABEL[value],
	}));

type CreateFormValues = Product & {
	area: Exclude<ProductArea, null> | null;
	parentProductId: number | null;
};

const CREATE_INITIAL_VALUES: CreateFormValues = {
	...PRODUCT_INITIAL_VALUES,
	area: null,
	parentProductId: null,
};

export const UpsertDeleteModal = ({
	product,
	modal,
	onHide,
	onNavigate,
}: Props) => {
	const [notificationMessage, setNotificationMessage] = useState("");
	const [error, setError] = useState("");
	const [showParentPicker, setShowParentPicker] = useState(false);

	const form = useForm<CreateFormValues>({
		defaultValues: CREATE_INITIAL_VALUES,
	});
	const { control, reset, handleSubmit, watch, setValue } = form;
	const getDirtyValues = useDirtyValues(form);

	const selectedArea = watch("area");
	const selectedParentId = watch("parentProductId");

	const { data: allProducts } = useQuery<Product[], Error>({
		queryKey: ["products"],
		queryFn: () => getProducts(),
		enabled: modal === "create",
		onError: (e: Error) => setError(e.message),
	});

	const parentOptions = useMemo(() => {
		if (!allProducts || !selectedArea) return [];
		const validAreas = VALID_PARENT_AREAS[selectedArea];
		if (validAreas.length === 0) return [];
		const validAreaSet = new Set(validAreas);
		return allProducts.filter((p) => {
			const area = getProductArea(p);
			return area != null && validAreaSet.has(area);
		});
	}, [allProducts, selectedArea]);

	const pickerDisabledAreas = useMemo(() => {
		if (!selectedArea) return undefined;
		const validSet = new Set(VALID_PARENT_AREAS[selectedArea]);
		const disabled: Partial<Record<Exclude<ProductArea, null>, string>> = {};
		const areaLabel = PRODUCT_AREA_LABEL[selectedArea];
		for (const a of ALL_PRODUCT_AREAS) {
			if (!validSet.has(a)) {
				disabled[a] =
					`${PRODUCT_AREA_LABEL[a]} products cannot be parent of a ${areaLabel} product`;
			}
		}
		return Object.keys(disabled).length > 0 ? disabled : undefined;
	}, [selectedArea]);

	const parentValue =
		(selectedParentId != null &&
			parentOptions.find((p) => p.ProductId === selectedParentId)) ||
		null;

	useEffect(() => {
		if (selectedParentId == null) return;
		const parent = parentOptions.find((p) => p.ProductId === selectedParentId);
		if (!parent) return;
		setValue("CustomerId", parent.CustomerId, { shouldDirty: true });
		setValue("SectionId", parent.SectionId, { shouldDirty: true });
		setValue("GradeId", parent.GradeId, { shouldDirty: true });
	}, [selectedParentId, parentOptions, setValue]);

	// Clear parent when area changes (previous parent may be incompatible)
	useEffect(() => {
		if (selectedParentId != null) {
			setValue("parentProductId", null, { shouldDirty: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only on area change
	}, [selectedArea]);

	const { deleteMutation, upsertMutation, isPending } = useProductMutations({
		getDirtyValues,
		activeAreaId: selectedArea
			? PRODUCT_AREA_ASSET_ID[selectedArea]
			: PRODUCT_AREA_ASSET_ID.weld,
		weldsAreaId: PRODUCT_AREA_ASSET_ID.weld,
		sawsAreaId: PRODUCT_AREA_ASSET_ID.saw,
		paintAreaId: PRODUCT_AREA_ASSET_ID.paint,
		punchAreaId: PRODUCT_AREA_ASSET_ID.punch,
		waxAreaId: PRODUCT_AREA_ASSET_ID.wax,
		onSuccess: (message, productId) => {
			setNotificationMessage(message);
			if (productId != null && onNavigate) {
				onNavigate(productId);
			}
			onHide(true);
		},
		onError: setError,
	});

	const onSubmit: SubmitHandler<CreateFormValues> = async (data) => {
		if (modal === "delete" && data.ProductId) {
			deleteMutation.mutate(data.ProductId);
			return;
		}
		upsertMutation.mutate(data);
	};

	useEffect(() => {
		if (modal === "create") {
			reset(CREATE_INITIAL_VALUES);
		} else if (modal === "delete" && product) {
			reset({
				...CREATE_INITIAL_VALUES,
				...product,
				area: getProductArea(product) ?? null,
				parentProductId: product.ParentProductId ?? null,
			});
		}
	}, [modal, product, reset]);

	const title = modal === "create" ? "NEW" : "DELETE";
	const isDelete = modal === "delete";

	return (
		<>
			{(modal === "create" || modal === "delete") && (
				<HelmetDexteel
					title={`${modal === "create" ? "New" : "Delete"} Product - Products`}
				/>
			)}
			<MesfModal
				id="modal-product"
				title={`${title} PRODUCT`}
				open={modal === "create" || modal === "delete"}
				handleClose={() => onHide(false)}
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }}>
						{isDelete && (
							<Typography color="error" sx={{ mb: 2 }}>
								Are you sure you want to delete this product?
							</Typography>
						)}
						<Grid2 container spacing={2}>
							{!isDelete && (
								<>
									<Grid2 size={{ md: 6, xs: 12 }}>
										<Controller
											name="area"
											control={control}
											rules={{ required: "Area is required" }}
											render={({ field, fieldState: { error } }) => (
												<Autocomplete
													size="small"
													options={AREA_OPTIONS}
													getOptionLabel={(opt) => opt.label}
													value={
														AREA_OPTIONS.find((o) => o.value === field.value) ??
														undefined
													}
													onChange={(_, opt) =>
														field.onChange(opt?.value ?? null)
													}
													isOptionEqualToValue={(a, b) => a.value === b.value}
													disabled={isDelete}
													disableClearable
													renderInput={(params) => (
														<TextField
															{...params}
															label="Area"
															size="small"
															error={!!error}
															helperText={error?.message}
														/>
													)}
												/>
											)}
										/>
									</Grid2>
									<Grid2
										size={{ md: 6, xs: 12 }}
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											visibility: canHaveParent(selectedArea)
												? "visible"
												: "hidden",
										}}
									>
										{parentValue ? (
											(() => {
												const parentArea = getProductArea(parentValue);
												const areaColor =
													parentArea != null
														? AREA_COLOR_MAP[PRODUCT_AREA_ASSET_ID[parentArea]]
														: "#9e9e9e";
												const areaLabel =
													parentArea != null
														? PRODUCT_AREA_LABEL[parentArea]
														: "—";
												return (
													<Box
														sx={{
															position: "relative",
															display: "flex",
															alignItems: "center",
															width: "100%",
															height: 40,
															border: "1px solid",
															borderColor: "rgba(0, 0, 0, 0.23)",
															borderRadius: 1,
															pl: 0.75,
															pr: 0.5,
															gap: 1,
														}}
													>
														<Typography
															component="span"
															sx={{
																position: "absolute",
																top: -8,
																left: 8,
																px: 0.5,
																fontSize: "0.75rem",
																color: "text.secondary",
																backgroundColor: "background.paper",
																lineHeight: 1,
																pointerEvents: "none",
															}}
														>
															Parent
														</Typography>
														<Box
															sx={{
																px: 1,
																py: 0.35,
																borderRadius: 0.75,
																backgroundColor: areaColor,
																color: "#fff",
																fontSize: "0.58rem",
																fontWeight: 700,
																textTransform: "uppercase",
																letterSpacing: "0.05em",
																minWidth: 48,
																textAlign: "center",
																flexShrink: 0,
															}}
														>
															{areaLabel}
														</Box>
														<Typography
															sx={{
																flex: 1,
																minWidth: 0,
																fontSize: "0.875rem",
																fontWeight: 600,
																overflow: "hidden",
																textOverflow: "ellipsis",
																whiteSpace: "nowrap",
															}}
														>
															{parentValue.PartNumber}
														</Typography>
														<IconButton
															size="small"
															onClick={() =>
																setValue("parentProductId", null, {
																	shouldDirty: true,
																})
															}
															sx={{ flexShrink: 0 }}
														>
															<CloseIcon fontSize="small" />
														</IconButton>
													</Box>
												);
											})()
										) : (
											<Button
												variant="outlined"
												size="medium"
												fullWidth
												startIcon={<AddIcon />}
												onClick={() => setShowParentPicker(true)}
											>
												Add Parent
											</Button>
										)}
									</Grid2>
								</>
							)}
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="PartNumber"
									control={control}
									rules={{ required: "Part Number is required" }}
									render={({ field, fieldState: { error } }) => (
										<TextField
											label="Part Number"
											variant="outlined"
											value={field.value || ""}
											onChange={field.onChange}
											error={!!error}
											helperText={error?.message}
											fullWidth
											autoComplete="off"
											size="small"
											disabled={isDelete}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="ExtendedPartNumber"
									control={control}
									rules={{ required: "Extended Part Number is required" }}
									render={({ field, fieldState: { error } }) => (
										<TextField
											label="Extended Part Number"
											variant="outlined"
											value={field.value || ""}
											onChange={(e) => field.onChange(e.target.value || null)}
											error={!!error}
											helperText={error?.message}
											fullWidth
											autoComplete="off"
											size="small"
											disabled={isDelete}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="GenericName"
									control={control}
									render={({ field }) => (
										<TextField
											label="Name"
											variant="outlined"
											value={field.value || ""}
											onChange={(e) => field.onChange(e.target.value || null)}
											fullWidth
											autoComplete="off"
											size="small"
											disabled={isDelete}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="CustomerId"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<CustomerSelector
											label="Customer"
											value={field.value}
											onChange={field.onChange}
											fieldError={error}
											disabled={isDelete}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="SectionId"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<CatalogSectionSelector
											label="Section"
											value={field.value}
											onChange={field.onChange}
											fieldError={error}
											disabled={isDelete}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 12 }}>
								<Controller
									name="GradeId"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<GradeSelector
											label="Grade"
											value={field.value}
											onChange={field.onChange}
											fieldError={error}
											disabled={isDelete}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="Comments"
									control={control}
									render={({ field }) => (
										<TextField
											label="Comments"
											variant="outlined"
											value={field.value || ""}
											onChange={field.onChange}
											fullWidth
											autoComplete="off"
											size="small"
											disabled={isDelete}
											multiline
											rows={2}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={isPending}
							onHide={onHide}
							buttonLabel={isDelete ? "Delete" : "Create"}
							buttonColor={isDelete ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ProductPickerModal
				open={showParentPicker}
				title="Select Parent"
				products={parentOptions}
				disabledAreas={pickerDisabledAreas}
				onSelect={(productId) => {
					setValue("parentProductId", productId, { shouldDirty: true });
					setShowParentPicker(false);
				}}
				onClose={() => setShowParentPicker(false)}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
